import { captureException } from '@sentry/nuxt';
import {
  GetNewsIndex,
  type GetNewsIndexQuery,
  type GetNewsIndexQueryVariables,
  type StrapiArticle,
  type StrapiArticlesIndex,
} from '#variant/apollo/types/types';

export default async () => {
  const { locale } = useI18n();
  const { redirectToDefaultLocale } = useRedirectToLocale();
  const indexFetched = ref(false);
  const { refresh: refreshNewsIndex, data: newsIndexData } = await useAsyncData(
    `gp-news-index-${locale.value}`,
    async () => {
      const ret = {
        articles: [] as Array<StrapiArticle>,
        articlesIndex: null as StrapiArticlesIndex,
      };

      try {
        if (!indexFetched.value) {
          const { data: articlesIndexData, error: articleIndexError } =
            await useLegacyApollo<
              GetNewsIndexQuery,
              GetNewsIndexQueryVariables
            >(GetNewsIndex, {
              locale: locale.value,
            });

          if (articlesIndexData.articleIndex == null) {
            return ret;
          }

          if (articleIndexError != null) {
            captureException(articleIndexError);
            return ret;
          }

          ret.articlesIndex =
            articlesIndexData.articleIndex as StrapiArticlesIndex;

          indexFetched.value = true;
        }
      } catch (error) {
        captureException(error);
      }

      return ret;
    },
  );

  if (newsIndexData.value.articlesIndex == null && locale.value !== 'en') {
    await redirectToDefaultLocale();
  }

  const newsIndex = computed(() => newsIndexData.value?.articlesIndex);
  const localizedSlugs = computed(
    () => newsIndexData.value?.articlesIndex?.localizedSlugs,
  );
  useI18nLocalizedSlugs(localizedSlugs);

  useHead({
    title: () => newsIndex.value?.metaTitle,
  });

  const config = useRuntimeConfig();
  const route = useRoute();
  useSeoMeta({
    title: () => newsIndex.value?.metaTitle,
    description: () => newsIndex.value?.metaDescription,
    ogImage: () => newsIndex.value?.headerCoverImage?.url,
    ogTitle: () => newsIndex.value?.metaTitle,
    ogDescription: () => newsIndex.value?.metaDescription,
    ogImageAlt: () => newsIndex.value?.headerCoverImage?.alternativeText,
    ogUrl: () => `${config.public.websiteUrl}${route.fullPath}`,
    twitterCard: 'summary_large_image',
    twitterImage: () => newsIndex.value?.headerCoverImage?.url,
    twitterTitle: () => newsIndex.value?.metaTitle,
    twitterDescription: () => newsIndex.value?.metaDescription,
    twitterImageAlt: () => newsIndex.value?.headerCoverImage?.alternativeText,
  });

  return {
    newsIndex,
    refreshNewsIndex,
  };
};
